import { captureException } from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { isElectron } from "@/lib/platform";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

function copyStylesheetsToPipWindow(pip: Window): void {
	const styleSheets = Array.from(document.styleSheets);
	for (const sheet of styleSheets) {
		try {
			if (sheet.href) {
				const link = pip.document.createElement("link");
				link.rel = "stylesheet";
				link.href = sheet.href;
				pip.document.head.appendChild(link);
			} else if (sheet.cssRules) {
				const style = pip.document.createElement("style");
				const cssText = Array.from(sheet.cssRules)
					.map((rule) => rule.cssText)
					.join("\n");
				style.textContent = cssText;
				pip.document.head.appendChild(style);
			}
		} catch (e) {
			console.warn("Could not copy stylesheet:", e);
			if (SENTRY_ENABLED)
				captureException(e as Error, {
					tags: { context: "electron_pip_copy_stylesheet" },
				});
		}
	}
}

function setupPipWindowStyles(pip: Window): void {
	pip.document.body.style.margin = "0";
	pip.document.body.style.padding = "0";
	pip.document.body.style.overflow = "hidden";
	pip.document.body.classList.add("dark");
}

export type UseElectronPipOptions = {
	width?: number;
	height?: number;
	onClose?: () => void;
};

export type UseElectronPipReturn = {
	isSupported: boolean;
	isPipActive: boolean;
	pipWindow: Window | null;
	openPip: () => boolean;
	closePip: () => void;
	pipContainerRef: React.RefObject<HTMLDivElement | null>;
};

export function useElectronPip(
	options: UseElectronPipOptions = {}
): UseElectronPipReturn {
	const { width = 320, height = 240, onClose } = options;

	const [isPipActive, setIsPipActive] = useState(false);
	const [pipWindow, setPipWindow] = useState<Window | null>(null);
	const pipContainerRef = useRef<HTMLDivElement | null>(null);
	const pipWindowRef = useRef<Window | null>(null);
	const closeCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
		null
	);
	const onCloseRef = useRef(onClose);
	const widthRef = useRef(width);
	const heightRef = useRef(height);

	onCloseRef.current = onClose;
	pipWindowRef.current = pipWindow;
	widthRef.current = width;
	heightRef.current = height;

	const isSupported = isElectron;

	const closePip = useCallback(() => {
		const win = pipWindowRef.current;
		pipWindowRef.current = null;
		// Unmount the portal synchronously BEFORE closing the window, so React does
		// not try to commit to a destroyed DOM, which crashes on Windows.
		flushSync(() => {
			setPipWindow(null);
			setIsPipActive(false);
		});
		if (win && !win.closed) {
			win.close();
		}
	}, []);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: PiP logic requires multiple state checks
	const openPip = useCallback((): boolean => {
		console.log("[ElectronPiP] openPip called, isElectron:", isElectron);
		if (!isElectron) {
			console.log("[ElectronPiP] Not Electron, returning false");
			return false;
		}

		const state = uiStore.getState();
		const broadcast = coreStore.getState().broadcast;
		const hasActiveCall = !!state.activeChannel;
		const hasActiveBroadcast =
			(!!state.activeBroadcastViewer || !!state.broadcastRoom) &&
			broadcast?.status === "live";
		console.log(
			"[ElectronPiP] hasActiveCall:",
			hasActiveCall,
			"hasActiveBroadcast:",
			hasActiveBroadcast
		);
		if (!(hasActiveCall || hasActiveBroadcast)) {
			console.log("[ElectronPiP] No active call or broadcast, returning false");
			return false;
		}

		if (pipWindowRef.current && !pipWindowRef.current.closed) {
			console.log("[ElectronPiP] PiP window already open, focusing");
			pipWindowRef.current.focus();
			return true;
		}

		try {
			const features = `width=${widthRef.current},height=${heightRef.current},elaso-pip=true`;
			console.log("[ElectronPiP] Opening window with features:", features);
			const pip = window.open("about:blank", "elaso-pip", features);

			if (!pip) {
				console.error(
					"[ElectronPiP] Failed to open PiP window: window.open returned null"
				);
				return false;
			}

			console.log(
				"[ElectronPiP] Window opened successfully, pip.closed:",
				pip.closed
			);
			copyStylesheetsToPipWindow(pip);
			setupPipWindowStyles(pip);

			pipWindowRef.current = pip;
			setPipWindow(pip);
			setIsPipActive(true);
			console.log("[ElectronPiP] State set: isPipActive=true");

			let closeHandled = false;
			const handleClose = (): void => {
				if (closeHandled) return;
				closeHandled = true;
				console.log("[ElectronPiP] handleClose called");
				if (closeCheckIntervalRef.current) {
					clearInterval(closeCheckIntervalRef.current);
					closeCheckIntervalRef.current = null;
				}
				pipWindowRef.current = null;
				// flushSync so React unmounts the portal before the window's document
				// is destroyed, which matters on Windows.
				flushSync(() => {
					setPipWindow(null);
					setIsPipActive(false);
				});
				onCloseRef.current?.();
			};

			// beforeunload fires synchronously before the window is destroyed, which
			// is the chance to unmount the portal before the DOM is gone. It matters
			// on Windows, where window destruction is immediate.
			pip.addEventListener("beforeunload", () => {
				console.log("[ElectronPiP] beforeunload event fired");
				handleClose();
			});

			pip.addEventListener("unload", () => {
				console.log("[ElectronPiP] unload event fired");
				handleClose();
			});

			pip.addEventListener("pagehide", () => {
				console.log(
					"[ElectronPiP] pagehide event fired, pip.closed:",
					pip.closed
				);
				handleClose();
			});

			// A fallback for the case where none of the events above fire.
			const CLOSE_CHECK_INTERVAL_MS = 500;
			closeCheckIntervalRef.current = setInterval(() => {
				if (pip.closed) {
					console.log("[ElectronPiP] Window closed detected via interval");
					handleClose();
				}
			}, CLOSE_CHECK_INTERVAL_MS);

			return true;
		} catch (error) {
			console.error("Failed to open Electron PiP window:", error);
			pipWindowRef.current = null;
			setPipWindow(null);
			setIsPipActive(false);
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "electron_pip_open" } });
			return false;
		}
	}, []);

	useEffect(
		() => () => {
			if (closeCheckIntervalRef.current) {
				clearInterval(closeCheckIntervalRef.current);
				closeCheckIntervalRef.current = null;
			}
			if (pipWindow && !pipWindow.closed) {
				pipWindow.close();
			}
		},
		[pipWindow]
	);

	return {
		isSupported,
		isPipActive,
		pipWindow,
		openPip,
		closePip,
		pipContainerRef,
	};
}
